import * as React from 'react';
import * as SplitPane from 'react-flex-split-pane';

import EditorCodeBar from '../../../containers/Editor/EditorCodeBar';
import { addModifiers } from '../../../shared/helpers';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { getLocalStorageItem, setLocalStorageItem } from '../../../shared/helpers/local-storage';
import { ToolTip } from '../../ToolTip';
import EditorBottomBarLeft from '../EditorBottomBar/EditorBottomBarLeft';

import SourceCodeEditor from './SourceCodeEditor';

interface CodeEditorProps {
  onCodeChange: (newValue: any, codeType: string) => void;
  fullWidth: boolean;
  isSimulating: boolean;
  handleCloneAlpha: () => void;
  showPropertiesTabModal: boolean;
  settingsOptions: any;
  handleShowPropertiesTabModalState: any;
  handleSimulate: () => void;
  isMultiTab: boolean;
  handleChangeVisualization: () => void;
  visualization: boolean;
  isPythonCode: boolean;
  fetchingPost: boolean;
  fetchingPercentage: boolean;
  handleExample: () => void;
  showExample: boolean;
  isConsultant: boolean;
  isVisualization: boolean;
  emptyCode: boolean;
  currentActiveTab: number;
  isFetching: boolean;
  data: any;
  tabs: any;
  userId: string;
}

interface CodeEditorState {
  size: number;
  isResizing: boolean;
  currentPercentageSize: number;
  themeColor: string;
}

export class SuperCodeEditor extends React.Component<CodeEditorProps, CodeEditorState> {
  state: CodeEditorState = {
    // 40 is the header size the other 40 are the tabs size and the last 60 is the bottom bar size
    size: window.innerHeight / 2 - 40 - 40 - 60,
    isResizing: false,
    currentPercentageSize: 0.5,
    themeColor: 'vs-dark',
  };

  componentDidMount() {
    const themeColor: any = getLocalStorageItem(this.props.userId, 'themeColor');
    if (themeColor) {
      this.setState({ themeColor: themeColor });
    }
    window.addEventListener('resize', this.resize);
  }

  handlerThemeLight = () => {
    setLocalStorageItem(this.props.userId, 'themeColor', 'light-theme');
    this.setState({ themeColor: 'light-theme' });
  };

  handlerThemeDark = () => {
    setLocalStorageItem(this.props.userId, 'themeColor', 'vs-dark');
    this.setState({ themeColor: 'vs-dark' });
  };

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
  }

  updateSize = () => {
    this.setState({ size: window.innerHeight * this.state.currentPercentageSize }, () => this.onResizeEnd());
  };

  resize = () => {
    this.setState({ isResizing: true }, () => this.updateSize());
  };

  onResizeStart = () => this.setState({ isResizing: true });
  onResizeEnd = () => this.setState({ isResizing: false });

  onChange = (size: any) => {
    this.setState(() => ({ size: size, currentPercentageSize: size / window.innerHeight }));
  };

  render() {
    const {
      onCodeChange,
      fullWidth,
      isSimulating,
      handleCloneAlpha,
      showPropertiesTabModal,
      settingsOptions,
      handleShowPropertiesTabModalState,
      handleSimulate,
      isMultiTab,
      handleChangeVisualization,
      visualization,
      fetchingPercentage,
      fetchingPost,
      isPythonCode,
      handleExample,
      showExample,
      isConsultant,
      isVisualization,
      emptyCode,
      currentActiveTab,
      isFetching,
      data,
      tabs,
    } = this.props;
    const themeColor = this.state.themeColor;

    return (
      <div className={`editor-code ${fullWidth ? 'editor-code--full-width' : ''}`}>
        <EditorCodeBar
          // @ts-expect-error withRouter(connect(...)) composition conflicts under @types/react@19:
          // connect's TOwnProps is `any`, collapsing the component's own-props to `never`.
          showPropertiesTabModal={showPropertiesTabModal}
          settingsOptions={settingsOptions}
          handleShowPropertiesTabModalState={handleShowPropertiesTabModalState}
        />
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        <SplitPane.default
          className="split-simulation"
          type="horizontal"
          onChange={this.onChange}
          size={this.state.size}
          isResizing={this.state.isResizing}
          onResizeStart={this.onResizeStart}
          onResizeEnd={this.onResizeEnd}
        >
          <div className={addModifiers('editor-code', themeColor ? [themeColor, 'top'] : ['vs-dark', 'top'])}>
            <div className="editor-code__title">
              <FormattedMessageHelper id="editor.selection.expression" />
              <ToolTip label="tooltip.editor.selection.expression.label" tooltipInfo="tooltip.editor.selection.expression.content" />
            </div>
            <SourceCodeEditor
              onCodeChange={onCodeChange}
              isSimulating={isSimulating}
              handleCloneAlpha={handleCloneAlpha}
              hasSelectionButton={true}
              isSuperAlpha={true}
              codeType="selection"
              handlerThemeLight={this.handlerThemeLight}
              handlerThemeDark={this.handlerThemeDark}
              themeColor={this.state.themeColor}
              isFetchingCheck={isFetching}
            />
          </div>
          <div className={addModifiers('editor-code', themeColor ? [themeColor, 'bottom'] : ['vs-dark', 'bottom'])}>
            <div className="editor-code__title">
              <FormattedMessageHelper id="editor.combo.expression" />
              <ToolTip label="tooltip.editor.combo.expression.label" tooltipInfo="tooltip.editor.combo.expression.content" />
            </div>
            <SourceCodeEditor
              onCodeChange={onCodeChange}
              isSimulating={isSimulating}
              handleCloneAlpha={handleCloneAlpha}
              codeType="combo"
              handlerThemeLight={this.handlerThemeLight}
              handlerThemeDark={this.handlerThemeDark}
              themeColor={this.state.themeColor}
            />
          </div>
        </SplitPane.default>
        <EditorBottomBarLeft
          handleSimulate={handleSimulate}
          isMultiTab={isMultiTab}
          handleChangeVisualization={handleChangeVisualization}
          visualization={visualization}
          isFetching={fetchingPost || fetchingPercentage}
          isFetchingCheck={isFetching}
          isPythonCode={isPythonCode}
          handleExample={handleExample}
          showExample={showExample}
          isConsultant={isConsultant}
          isVisualization={isVisualization}
          emptyCode={emptyCode}
          handleCloneAlpha={handleCloneAlpha}
          currentActiveTab={currentActiveTab}
          data={data}
          tabs={tabs}
          simulationType="super"
        />
      </div>
    );
  }
}

export default SuperCodeEditor;
